import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAllInfluencers } from "@/hooks/useAllInfluencers";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { useOrganization } from "@/contexts/OrganizationContext";
import { useAuth } from "@/contexts/AuthContext";
import { convertDisplayToDb } from "@/utils/currency";
import {
  type AddInfluencerModalProps,
  type AddInfluencerMode,
  type AddCampaignInfluencerPayload,
  type InfluencerCampaignStatus,
  AddInfluencerMethodChooser,
  ExistingInfluencerForm,
} from "./add-influencer";

export type { AddInfluencerModalProps };

type Props = AddInfluencerModalProps;

export const AddInfluencerModal: React.FC<Props> = ({
  open,
  onOpenChange,
  campaignId,
  assignedInfluencerIds,
  onSelectAddByHandle,
  onSelectImportCsv,
}) => {
  const { userCountry } = useAuth();
  const [mode, setMode] = useState<AddInfluencerMode>("choose");
  const [selectedInfluencer, setSelectedInfluencer] = useState("");
  const [status, setStatus] = useState<InfluencerCampaignStatus>("contacted");
  const [deliverables, setDeliverables] = useState("");
  const [fee, setFee] = useState("");
  const [notes, setNotes] = useState("");

  const [influencerSearch, setInfluencerSearch] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(true);

  useEffect(() => {
    if (open) {
      setMode("choose");
      setInfluencerSearch("");
      setIsDropdownOpen(true);
    }
  }, [open]);

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      setMode("choose");
      setInfluencerSearch("");
      setIsDropdownOpen(true);
    }
    onOpenChange(nextOpen);
  };

  const handleSelectAddByHandle = () => {
    onOpenChange(false);
    setMode("choose");
    onSelectAddByHandle();
  };

  const handleSelectImportCsv = () => {
    onOpenChange(false);
    setMode("choose");
    onSelectImportCsv();
  };

  const {
    data: influencers = [],
    isError: isInfluencersError,
    isLoading: isLoadingInfluencers,
  } = useAllInfluencers();
  const { currentOrganization } = useOrganization();
  const queryClient = useQueryClient();

  // Available influencers (not already assigned to this campaign)
  const availableInfluencers = (influencers || []).filter(
    (influencer) =>
      influencer && !(assignedInfluencerIds || []).includes(influencer.id)
  );

  const addInfluencerMutation = useMutation({
    mutationFn: async (data: AddCampaignInfluencerPayload) => {
      const { error } = await supabase
        .from("campaign_influencers")
        .insert([
          {
            ...data,
            status: data.status,
          },
        ]);

      if (error) {
        console.error("Insert error:", error);
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["campaign-influencers", campaignId],
      });
      toast({
        title: "Success",
        description: "Influencer added to campaign successfully",
      });
      onOpenChange(false);
      // Reset form
      setSelectedInfluencer("");
      setStatus("contacted");
      setDeliverables("");
      setFee("");
      setNotes("");
    },
    onError: (error: unknown) => {
      console.error("Error adding influencer to campaign:", error);
      const message =
        error && typeof error === "object" && "message" in error
          ? String(error.message)
          : "Failed to add influencer to campaign";
      toast({
        title: "Error",
        description: message,
        variant: "destructive",
      });
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedInfluencer) {
      toast({
        title: "Error",
        description: "Please select an influencer",
        variant: "destructive",
      });
      return;
    }

    if (!currentOrganization?.id) {
      toast({
        title: "Error",
        description:
          "No organization selected. Please select an organization first.",
        variant: "destructive",
      });
      return;
    }

    const feeCommitted = fee ? parseFloat(fee) : undefined;
    const dbFeeCommitted =
      feeCommitted !== undefined
        ? convertDisplayToDb(feeCommitted, userCountry)
        : undefined;

    const data: AddCampaignInfluencerPayload = {
      campaign_id: campaignId,
      influencer_id: selectedInfluencer,
      status: status,
      deliverables: deliverables || undefined,
      fee_committed: dbFeeCommitted,
      notes: notes || undefined,
    };

    addInfluencerMutation.mutate(data);
  };

  if (mode === "choose") {
    return (
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="max-w-[540px] p-6 sm:p-7 overflow-hidden font-hanken rounded-[24px] border border-slate-100 bg-white shadow-2xl">
          <AddInfluencerMethodChooser
            onSelectExisting={() => setMode("existing")}
            onSelectAddByHandle={handleSelectAddByHandle}
            onSelectImportCsv={handleSelectImportCsv}
          />
        </DialogContent>
      </Dialog>
    );
  }

  // Show loading or error state if no organization
  if (!currentOrganization) {
    return (
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Influencer to Campaign</DialogTitle>
          </DialogHeader>
          <div className="p-6 text-center">
            <p className="text-gray-600">
              Please select an organization first.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  const emptyInfluencerMessage = isInfluencersError
    ? "Unable to load influencers. Please refresh and try again."
    : influencers.length === 0
      ? "No influencers found in this organization."
      : "All influencers are already assigned to this campaign.";

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-2xl p-6 sm:p-7 font-hanken rounded-[20px] bg-white shadow-xl">
        <ExistingInfluencerForm
          onBack={() => setMode("choose")}
          influencers={availableInfluencers}
          selectedInfluencerId={selectedInfluencer}
          onSelectInfluencer={setSelectedInfluencer}
          isLoadingInfluencers={isLoadingInfluencers}
          isInfluencersError={isInfluencersError}
          emptyInfluencerMessage={emptyInfluencerMessage}
          influencerSearch={influencerSearch}
          onInfluencerSearchChange={setInfluencerSearch}
          isDropdownOpen={isDropdownOpen}
          onToggleDropdown={() => setIsDropdownOpen((prev) => !prev)}
          onOpenDropdown={() => setIsDropdownOpen(true)}
          status={status}
          onStatusChange={(val) => setStatus(val as InfluencerCampaignStatus)}
          fee={fee}
          onFeeChange={setFee}
          userCountry={userCountry}
          deliverables={deliverables}
          onDeliverablesChange={setDeliverables}
          notes={notes}
          onNotesChange={setNotes}
          onSubmit={handleSubmit}
          onCancel={() => onOpenChange(false)}
          isPending={addInfluencerMutation.isPending}
        />
      </DialogContent>
    </Dialog>
  );
};

export default AddInfluencerModal;
